import { Entity, ListValue } from '../data-objects'
import {
  EntityModel,
  FieldDefinition,
  FieldType,
  FormFieldDefinition,
  FormModel,
  ListFieldDefinition,
} from '../definitions'
import { EntityContext } from '../entity-context'
import { FormNode } from './form-control'
import { findNode } from './form-tree'

export type ControlBuilder = () => FormNode

export interface ControlArgs {
  fieldDef: FieldDefinition
  formDef: FormFieldDefinition
  entity: EntityContext | undefined
  prevChildren: FormNode | undefined
}

export function getControl(
  args: ControlArgs,
  entityModel: EntityModel,
  formModel: FormModel,
  rootFormModelName: string,
): FormNode {
  switch (args.fieldDef.fieldDefinitionType) {
    case 'List':
      return listControlBuilder(args, rootFormModelName)
    case 'Numeric': {
      const control = defaultControlBuilder(args, rootFormModelName)
      const rVal =
        entityModel.getNumericValidation(args.fieldDef.name) || formModel.getNumericValidation(args.formDef.name)
      if (rVal) {
        control.value.range = {
          min: rVal.min,
          max: rVal.max,
        }
      }
      return control
    }
    case 'OptionSet': {
      const control = defaultControlBuilder(args, rootFormModelName)
      control.value.selectOptions = {
        keep: [],
        remove: [],
      }
      return control
    }
    default:
      return defaultControlBuilder(args, rootFormModelName)
  }
}

export function getViewControl(args: Omit<ControlArgs, 'fieldDef'>, rootFormModelName: string): FormNode {
  const { formDef, entity, prevChildren } = args
  const control: FormNode = {
    id: formDef.name,
    nextSibling: prevChildren,
    value: {
      data: null,
      type: 'View',
      state: 'Enabled',
      visibility: 'Visible',
      validationMessages: [],
      validationParams: [],
      validationMessageParams: [],
      validationState: 'Valid',
      formDefinition: formDef,
      isDirty: false,
      entityId: entity?.id,
      entityName: entity?.entityName as string,
      validationFlags: 0,
      rootFormModelName,
    },
  }
  return control
}

export function getFormControl(args: Omit<ControlArgs, 'fieldDef'>, rootFormModelName: string): FormNode {
  const { formDef, entity, prevChildren } = args
  const control: FormNode = {
    id: formDef.name,
    nextSibling: prevChildren,
    value: {
      data: null,
      type: 'Form',
      state: 'Enabled',
      visibility: 'Visible',
      validationMessages: [],
      validationParams: [],
      validationState: 'Valid',
      formDefinition: formDef,
      isDirty: false,
      entityId: entity?.id,
      entityName: entity?.entityName as string,
      validationFlags: 0,
      rootFormModelName,
    },
  }
  return control
}

function defaultControlBuilder(args: ControlArgs, rootFormModelName: string): FormNode {
  const { fieldDef, formDef, entity, prevChildren } = args
  const control: FormNode = {
    id: formDef.name,
    nextSibling: prevChildren,
    value: {
      data: entity?.fields ? entity.fields[formDef.name] : null,
      type: geFieldControlType(fieldDef, formDef),
      state: 'Enabled',
      visibility: 'Visible',
      validationMessages: [],
      validationParams: [],
      validationMessageParams: [],
      validationState: 'Valid',
      formDefinition: formDef,
      fieldDefinition: fieldDef,
      isDirty: false,
      entityId: entity?.id,
      entityName: entity?.entityName as string,
      validationFlags: 0,
      rootFormModelName,
    },
  }
  return control
}

function listControlBuilder(args: ControlArgs, rootFormModelName: string): FormNode {
  const { fieldDef, formDef, entity, prevChildren } = args
  const control: FormNode = {
    id: formDef.name,
    nextSibling: prevChildren,
    value: {
      data: entity?.fields ? entity.fields[formDef.name] : null,
      type: geFieldControlType(fieldDef, formDef),
      state: 'Enabled',
      visibility: 'Visible',
      validationMessages: [],
      validationParams: [],
      validationMessageParams: [],
      validationState: 'Valid',
      formDefinition: formDef,
      fieldDefinition: fieldDef,
      isDirty: false,
      entityId: entity?.id,
      entityName: entity?.entityName as string,
      validationFlags: 0,
      rootFormModelName,
    },
  }

  const fieldValue = entity?.fields ? (entity.fields[formDef.name] as ListValue) : undefined
  buildTree(entity?.id, control, fieldValue, fieldDef as ListFieldDefinition, formDef, rootFormModelName)

  return control
}

function buildTree(
  entityId: string | undefined,
  n: FormNode,
  r: ListValue | undefined,
  fieldDef: ListFieldDefinition,
  formDef: FormFieldDefinition,
  rootFormModelName: string,
) {
  let lastNode: FormNode

  if (r?.value?.length === 0) {
    n.firstChild = undefined
  } else {
    r?.value?.forEach((e, idx) => {
      const existingRow = findNode(n, e.id)
      const newNode = buildRow(entityId, fieldDef, formDef, existingRow, rootFormModelName, e)
      if (idx === 0) {
        n.firstChild = newNode
      } else {
        lastNode.nextSibling = newNode
      }
      lastNode = newNode
    })
  }
}

function buildRow(
  entityId: string | undefined,
  fieldDef: ListFieldDefinition,
  formDef: FormFieldDefinition,
  existingNode: FormNode | null,
  rootFormModelName: string,
  e?: Entity,
): FormNode {
  const newNode: FormNode = existingNode ?? {
    id: e ? e.id : crypto.randomUUID(),
    value: {
      data: null,
      isDirty: false,
      state: 'Enabled',
      visibility: 'Visible',
      type: 'Group',
      validationMessages: [],
      validationParams: [],
      validationState: 'Valid',
      validationFlags: 0,
      entityName: e?.entityName as string,
      rootFormModelName,
    },
  }
  // create child
  let lastNodeField: FormNode
  fieldDef.fields.forEach((fd, idx) => {
    const fieldNode = findNode(existingNode as FormNode, fd.name)
    const newNodeField: FormNode = {
      id: fd.name, // TODO check
      value: {
        data: e ? e.fields[fd.name] : null,
        isDirty: false,
        state: fieldNode?.value?.state ?? 'Enabled',
        visibility: fieldNode?.value?.visibility ?? 'Visible',
        type: fd.fieldDefinitionType,
        validationMessages: fieldNode?.value?.validationMessages ?? [],
        validationParams: fieldNode?.value?.validationParams ?? [],
        validationMessageParams: fieldNode?.value?.validationMessageParams ?? [],
        validationState: fieldNode?.value?.validationState ?? 'Valid',
        validationRevealed: fieldNode?.value?.validationRevealed ?? false,
        fieldDefinition: fd,
        formDefinition: formDef,
        entityId,
        rowId: newNode.id,
        entityName: e?.entityName as string,
        listName: formDef.name,
        validationFlags: 0,
        rootFormModelName,
      },
    }
    if (idx === 0) {
      newNode.firstChild = newNodeField
    } else {
      lastNodeField.nextSibling = newNodeField
    }
    lastNodeField = newNodeField
  })
  return newNode
}

function geFieldControlType(fieldDef: FieldDefinition, formDef: FormFieldDefinition): FieldType {
  if (formDef.customUI) return 'Custom'
  if (formDef.type === 'MultiSelector') return 'MultiSelector'
  if (formDef.type === 'MetadataSelector') return 'MetadataSelector'
  return fieldDef.fieldDefinitionType
}
